import React, { useState, useRef, useEffect } from 'react';
import { InventoryDocument, ImageInfo, InventoryUpdateVo } from '../../types';
import { Upload, X, ArrowLeft, Save, Image as ImageIcon } from 'lucide-react';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';
import heic2any from 'heic2any';
import { heicTo } from 'heic-to';

interface EditInventoryImagesPageProps {
  inventory: InventoryDocument;
  onBack: () => void;
  onSave: (updates: InventoryUpdateVo) => Promise<void>;
  loading: boolean;
}

export const EditInventoryImagesPage: React.FC<EditInventoryImagesPageProps> = ({
  inventory,
  onBack,
  onSave,
  loading,
}) => {
  // Helper function to transform image list
  const transformImageList = (list: any[]): ImageInfo[] => {
    return (list || []).map((img: any) => ({
      endPoint: img.url || img.endPoint || '',
      caption: img.caption || '',
      date: img.date || '',
      tag: img.tag || '',
      cloudName: img.cloudName || '',
      surl: img.surl || '',
      murl: img.murl || '',
      lurl: img.lurl || '',
    }));
  };

  // Tab type
  type ImageListType = 'imageList' | 'stayImageList' | 'vehicleImageList' | 'tripImageList';
  
  const [activeTab, setActiveTab] = useState<ImageListType>('imageList');
  const [imageList, setImageList] = useState<ImageInfo[]>(transformImageList(inventory.imageList || []));
  const [stayImageList, setStayImageList] = useState<ImageInfo[]>(transformImageList(inventory.stayImageList || []));
  const [vehicleImageList, setVehicleImageList] = useState<ImageInfo[]>(transformImageList(inventory.vehicleImageList || []));
  const [tripImageList, setTripImageList] = useState<ImageInfo[]>(transformImageList(inventory.tripImageList || []));
  const [mainImage, setMainImage] = useState<string>(inventory.image || '');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingMultiple, setUploadingMultiple] = useState(false);
  const [uploadStats, setUploadStats] = useState({ current: 0, total: 0, completed: 0, failed: 0 });
  const [saving, setSaving] = useState(false);
  const [editingUrlsIndex, setEditingUrlsIndex] = useState<number | null>(null);
  const [editingUrls, setEditingUrls] = useState<{ endPoint: string; surl: string; murl: string; lurl: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get current active list based on tab
  const getCurrentImageList = (): ImageInfo[] => {
    switch (activeTab) {
      case 'stayImageList':
        return stayImageList;
      case 'vehicleImageList':
        return vehicleImageList;
      case 'tripImageList':
        return tripImageList;
      default:
        return imageList;
    }
  };

  // Set current active list based on tab
  const setCurrentImageList = (newList: ImageInfo[] | ((prev: ImageInfo[]) => ImageInfo[])) => {
    switch (activeTab) {
      case 'stayImageList':
        setStayImageList(newList);
        break;
      case 'vehicleImageList':
        setVehicleImageList(newList);
        break;
      case 'tripImageList':
        setTripImageList(newList);
        break;
      default:
        setImageList(newList);
    }
  };

  // Update all image lists when inventory changes
  useEffect(() => {
    setImageList(transformImageList(inventory.imageList || []));
    setStayImageList(transformImageList(inventory.stayImageList || []));
    setVehicleImageList(transformImageList(inventory.vehicleImageList || []));
    setTripImageList(transformImageList(inventory.tripImageList || []));
    setMainImage(inventory.image || '');
  }, [inventory]);

  // Convert HEIC/HEIF files to JPG
  const convertHeicToJpg = async (file: File): Promise<File> => {
    const isHeic = /\.(heic|heif)$/i.test(file.name) || 
                   file.type === 'image/heic' || 
                   file.type === 'image/heif';
    
    if (!isHeic) {
      return file; // Return original file if not HEIC
    }

    // Try heic2any first (faster, but may not support newer formats)
    if (typeof heic2any === 'function') {
      try {
        const convertedBlob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.92,
        });

        // heic2any can return an array or a single blob
        const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
        
        if (!blob) {
          throw new Error('Conversion returned empty result');
        }

        // Create a new File object with JPG extension
        const fileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
        const convertedFile = new File([blob], fileName, {
          type: 'image/jpeg',
          lastModified: file.lastModified,
        });

        return convertedFile;
      } catch (error: any) {
        const errorCode = error?.code;
        const errorMessage = error?.message || error?.toString() || 'Unknown error';
        
        // If heic2any fails with format not supported, try heic-to as fallback
        if (errorCode === 2 || errorMessage.includes('ERR_LIBHEIF format not supported') || errorMessage.includes('format not supported')) {
          console.log('heic2any failed, trying heic-to as fallback...');
          // Fall through to try heic-to
        } else {
          // For other errors, throw immediately
          throw error;
        }
      }
    }

    // Fallback to heic-to (supports newer HEIC formats)
    if (typeof heicTo === 'function') {
      try {
        const convertedBlob = await heicTo({
          blob: file,
          type: 'image/jpeg',
          quality: 0.92,
        });

        if (!convertedBlob) {
          throw new Error('Conversion returned empty result');
        }

        // Create a new File object with JPG extension
        const fileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
        const convertedFile = new File([convertedBlob], fileName, {
          type: 'image/jpeg',
          lastModified: file.lastModified,
        });

        return convertedFile;
      } catch (error: any) {
        const errorMessage = error?.message || error?.toString() || 'Unknown error';
        console.error('Error converting HEIC to JPG with heic-to:', {
          error,
          message: errorMessage,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
        });
        
        toast.error(
          'Failed to convert HEIC file. This format may not be supported. Please convert it to JPG using your device\'s Photos app or an online converter before uploading.',
          { duration: 6000 }
        );
        throw error;
      }
    }

    // If neither library is available
    toast.error('HEIC conversion is not available. Please convert the file manually.');
    throw new Error('HEIC conversion libraries not available');
  };

  const handleImageUpload = async (file: File) => {
    // Check if it's an image file by MIME type or extension (for HEIC support)
    const isImageByType = file.type.startsWith('image/');
    const isImageByExtension = /\.(jpg|jpeg|png|gif|webp|heic|heif)$/i.test(file.name);
    
    if (!isImageByType && !isImageByExtension) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 30 * 1024 * 1024) {
      toast.error('Image size should be less than 30MB');
      return;
    }

    try {
      setUploadingImage(true);
      setUploadProgress(0);

      // Convert HEIC to JPG if needed
      const fileToUpload = await convertHeicToJpg(file);

      // Use uploadToImageCloudinary (same as Image Upload section)
      const result = await apiService.uploadToImageCloudinary(fileToUpload, (progress) => {
        setUploadProgress(progress);
      });

      // Extract file extension from the converted filename (will be .jpg for HEIC)
      const fileExt = fileToUpload.name.split('.').pop()?.toLowerCase() || 'jpg';
      
      // Generate transformation URLs (small, medium, large)
      const surl = `https://img.bozapp.in/t/w_400/${result.id}.${fileExt}`;  // Small
      const murl = `https://img.bozapp.in/t/w_800/${result.id}.${fileExt}`;  // Medium
      const lurl = `https://img.bozapp.in/t/w_1200/${result.id}.${fileExt}`; // Large

      // Use original_url from the response
      const newImage: ImageInfo = {
        endPoint: result.original_url,
        caption: '',
        date: '',
        tag: '',
        surl: surl,
        murl: murl,
        lurl: lurl,
      };

      setCurrentImageList((prev) => [...prev, newImage]);
      toast.success('Image uploaded successfully');
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      setUploadProgress(0);
    }
  };

  const handleMultipleImageUpload = async (files: FileList) => {
    const fileArray = Array.from(files);
    
    // Validate all files first
    const validFiles: File[] = [];
    const errors: string[] = [];
    
      fileArray.forEach((file) => {
      // Check if it's an image file by MIME type or extension (for HEIC support)
      const isImageByType = file.type.startsWith('image/');
      const isImageByExtension = /\.(jpg|jpeg|png|gif|webp|heic|heif)$/i.test(file.name);
      
      if (!isImageByType && !isImageByExtension) {
        errors.push(`${file.name}: Not an image file`);
        return;
      }
      if (file.size > 30 * 1024 * 1024) {
        errors.push(`${file.name}: Size exceeds 30MB`);
        return;
      }
      validFiles.push(file);
    });

    if (errors.length > 0) {
      toast.error(`Some files were skipped:\n${errors.join('\n')}`);
    }

    if (validFiles.length === 0) {
      return;
    }

    try {
      setUploadingMultiple(true);
      setUploadStats({
        current: 0,
        total: validFiles.length,
        completed: 0,
        failed: 0,
      });

      const uploadedImages: ImageInfo[] = [];
      let failedCount = 0;
      let completedCount = 0;

      // Upload files sequentially to avoid overwhelming the server
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        setUploadStats((prev) => ({
          ...prev,
          current: i + 1,
        }));

        try {
          // Convert HEIC to JPG if needed
          const fileToUpload = await convertHeicToJpg(file);

          const result = await apiService.uploadToImageCloudinary(fileToUpload, (progress) => {
            // Calculate overall progress across all files
            const fileProgress = progress / 100;
            const overallProgress = ((i + fileProgress) / validFiles.length) * 100;
            setUploadProgress(overallProgress);
          });

          // Extract file extension from the converted filename (will be .jpg for HEIC)
          const fileExt = fileToUpload.name.split('.').pop()?.toLowerCase() || 'jpg';
          
          // Generate transformation URLs (small, medium, large)
          const surl = `https://img.bozapp.in/t/w_400/${result.id}.${fileExt}`;  // Small
          const murl = `https://img.bozapp.in/t/w_800/${result.id}.${fileExt}`;  // Medium
          const lurl = `https://img.bozapp.in/t/w_1200/${result.id}.${fileExt}`; // Large

          const newImage: ImageInfo = {
            endPoint: result.original_url,
            caption: '',
            date: '',
            tag: '',
            surl: surl,
            murl: murl,
            lurl: lurl,
          };

          uploadedImages.push(newImage);
          completedCount++;
          setUploadStats((prev) => ({
            ...prev,
            completed: completedCount,
          }));
        } catch (error: any) {
          console.error(`Failed to upload ${file.name}:`, error);
          failedCount++;
          setUploadStats((prev) => ({
            ...prev,
            failed: failedCount,
          }));
        }
      }

      // Add all uploaded images to the list
      if (uploadedImages.length > 0) {
        setCurrentImageList((prev) => [...prev, ...uploadedImages]);
        toast.success(`Successfully uploaded ${uploadedImages.length} image${uploadedImages.length > 1 ? 's' : ''}`);
      }

      if (failedCount > 0) {
        toast.error(`${failedCount} image${failedCount > 1 ? 's' : ''} failed to upload`);
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload images');
    } finally {
      setUploadingMultiple(false);
      setUploadProgress(0);
      setUploadStats({ current: 0, total: 0, completed: 0, failed: 0 });
    }
  };

  const removeImage = (index: number) => {
    const currentList = getCurrentImageList();
    const removedImage = currentList[index];
    setCurrentImageList((prev) => prev.filter((_, i) => i !== index));
    
    // If removed image was the main image, clear main image
    if (removedImage.endPoint === mainImage) {
      setMainImage('');
    }
    
    toast.success('Image removed');
  };

  const updateImageInfo = (index: number, field: keyof ImageInfo, value: any) => {
    setCurrentImageList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const openUrlEditor = (index: number) => {
    const currentList = getCurrentImageList();
    const img = currentList[index];
    setEditingUrlsIndex(index);
    setEditingUrls({
      endPoint: img.endPoint || '',
      surl: img.surl || '',
      murl: img.murl || '',
      lurl: img.lurl || '',
    });
  };

  const closeUrlEditor = () => {
    setEditingUrlsIndex(null);
    setEditingUrls(null);
  };

  const saveUrlsFromModal = () => {
    if (editingUrlsIndex === null || !editingUrls) return;

    const currentList = getCurrentImageList();
    const img = currentList[editingUrlsIndex];
    
    // Update all URL fields
    setCurrentImageList((prev) => {
      const updated = [...prev];
      updated[editingUrlsIndex] = {
        ...img,
        endPoint: editingUrls.endPoint,
        surl: editingUrls.surl,
        murl: editingUrls.murl,
        lurl: editingUrls.lurl,
      };
      return updated;
    });

    // If the endPoint changed and it was the main image, update main image
    if (img.endPoint === mainImage && editingUrls.endPoint !== mainImage) {
      setMainImage(editingUrls.endPoint);
    }

    toast.success('URLs updated successfully');
    closeUrlEditor();
  };

  const setMainImageFromList = (url: string) => {
    setMainImage(url);
    toast.success('Main image updated');
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Transform all image lists from endPoint to url for API
      const transformList = (list: ImageInfo[]) => list.map((img: ImageInfo) => ({
        endPoint: img.endPoint,
        url: img.endPoint, // Also include url for API compatibility
        caption: img.caption || '',
        date: img.date || '',
        tag: img.tag || '',
        cloudName: img.cloudName || '',
        surl: img.surl || '',
        murl: img.murl || '',
        lurl: img.lurl || '',
      }));

      const updates: InventoryUpdateVo = {
        imageList: transformList(imageList) as any,
        stayImageList: transformList(stayImageList) as any,
        vehicleImageList: transformList(vehicleImageList) as any,
        tripImageList: transformList(tripImageList) as any,
        image: mainImage,
      };

      await onSave(updates);
      toast.success('Images saved successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to save images');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 p-6 pb-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              disabled={saving || loading}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Edit Images - {inventory.title}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Manage images for this inventory
              </p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || loading || uploadingImage || uploadingMultiple}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Tabs */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="flex -mb-px" aria-label="Tabs">
                {[
                  { id: 'imageList' as ImageListType, label: 'All Images', count: imageList.length },
                  { id: 'stayImageList' as ImageListType, label: 'Stay Images', count: stayImageList.length },
                  { id: 'vehicleImageList' as ImageListType, label: 'Vehicle Images', count: vehicleImageList.length },
                  { id: 'tripImageList' as ImageListType, label: 'Trip Images', count: tripImageList.length },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex-1 px-6 py-4 text-sm font-medium border-b-2 transition-colors
                      ${
                        activeTab === tab.id
                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }
                    `}
                  >
                    {tab.label} ({tab.count})
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Upload Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                Upload New Image
              </h2>
            </div>
            
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.heic,.heif"
                multiple
                onChange={(e) => {
                  const files = e.target.files;
                  if (files && files.length > 0) {
                    if (files.length === 1) {
                      handleImageUpload(files[0]);
                    } else {
                      handleMultipleImageUpload(files);
                    }
                  }
                }}
                className="hidden"
                disabled={loading || uploadingImage || uploadingMultiple}
              />
              <label
                htmlFor="image-upload"
                className="cursor-pointer flex flex-col items-center gap-4"
              >
                {(uploadingImage || uploadingMultiple) ? (
                  <>
                    <div className="w-full max-w-md space-y-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                          <span className="text-gray-700 dark:text-gray-300 font-medium">
                            {uploadingMultiple ? (
                              <>Uploading {uploadStats.current} of {uploadStats.total}</>
                            ) : (
                              <>Uploading...</>
                            )}
                          </span>
                        </div>
                        <span className="text-gray-600 dark:text-gray-400 font-medium">
                          {Math.round(uploadProgress)}%
                        </span>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                        <div
                          className="bg-blue-600 h-full rounded-full transition-all duration-300 ease-out"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      
                      {uploadingMultiple && (
                        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                          <span>✓ Completed: {uploadStats.completed}</span>
                          {uploadStats.failed > 0 && (
                            <span className="text-red-600 dark:text-red-400">✗ Failed: {uploadStats.failed}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="p-4 bg-blue-100 dark:bg-blue-900 rounded-full">
                      <Upload className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-gray-700 dark:text-gray-300 font-medium">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        PNG, JPG, GIF, HEIC up to 30MB each (Multiple files supported)
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Select Images
                    </button>
                  </>
                )}
              </label>
            </div>
          </div>

          {/* Images Grid */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {activeTab === 'imageList' ? 'All Images' : 
                 activeTab === 'stayImageList' ? 'Stay Images' :
                 activeTab === 'vehicleImageList' ? 'Vehicle Images' : 'Trip Images'} ({getCurrentImageList().length})
              </h2>
              {mainImage && activeTab === 'imageList' && (
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Main Image: {imageList.findIndex(img => img.endPoint === mainImage) + 1 || 'Not set'}
                </div>
              )}
            </div>

            {getCurrentImageList().length === 0 ? (
              <div className="text-center py-12">
                <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  No images uploaded yet. Upload your first image above.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {getCurrentImageList().map((img, index) => (
                  <div
                    key={index}
                    className={`border-2 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-700 transition-all ${
                      img.endPoint === mainImage
                        ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                  >
                    {/* Image */}
                    <div className="relative aspect-video bg-gray-200 dark:bg-gray-800">
                      <img
                        src={img.surl || img.endPoint}
                        alt={img.caption || `Image ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback to endPoint if surl fails
                          if (e.currentTarget.src !== img.endPoint) {
                            e.currentTarget.src = img.endPoint;
                          } else {
                            e.currentTarget.style.display = 'none';
                          }
                        }}
                      />
                      
                      {/* Main Image Badge */}
                      {img.endPoint === mainImage && activeTab === 'imageList' && (
                        <div className="absolute top-2 left-2 px-2 py-1 bg-blue-600 text-white text-xs font-medium rounded">
                          Main Image
                        </div>
                      )}

                      {/* Actions */}
                      <div className="absolute top-2 right-2 flex gap-2">
                        {img.endPoint !== mainImage && activeTab === 'imageList' && (
                          <button
                            type="button"
                            onClick={() => setMainImageFromList(img.endPoint)}
                            className="p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                            disabled={loading || saving}
                            title="Set as Main Image"
                          >
                            <ImageIcon className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="p-1.5 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                          disabled={loading || saving}
                          title="Delete Image"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Image Info */}
                    <div className="p-4 space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Caption
                        </label>
                        <input
                          type="text"
                          value={img.caption || ''}
                          onChange={(e) => updateImageInfo(index, 'caption', e.target.value)}
                          placeholder="Enter caption"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          disabled={loading || saving}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Tag
                        </label>
                        <input
                          type="text"
                          value={img.tag || ''}
                          onChange={(e) => updateImageInfo(index, 'tag', e.target.value)}
                          placeholder="Enter tag"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          disabled={loading || saving}
                        />
                      </div>

                      <div className="pt-2 border-t border-gray-200 dark:border-gray-600 space-y-2">
                        <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                          URLs
                        </div>
                        {[
                          { label: 'URL', field: 'endPoint' as keyof ImageInfo, value: img.endPoint },
                          { label: 'Small (surl)', field: 'surl' as keyof ImageInfo, value: img.surl || '' },
                          { label: 'Medium (murl)', field: 'murl' as keyof ImageInfo, value: img.murl || '' },
                          { label: 'Large (lurl)', field: 'lurl' as keyof ImageInfo, value: img.lurl || '' },
                        ].map(({ label, value }) => (
                          <div key={label} className="space-y-1">
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                              {label}
                            </label>
                            <div className="flex items-center gap-1">
                              <input
                                type="text"
                                value={value}
                                onFocus={() => openUrlEditor(index)}
                                readOnly
                                className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-white font-mono cursor-pointer hover:border-blue-400 transition-colors"
                                placeholder={`Enter ${label.toLowerCase()}`}
                                disabled={loading || saving}
                                title="Click to edit all URLs in popup"
                              />
                              {value && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      navigator.clipboard.writeText(value);
                                      toast.success(`${label} copied to clipboard`);
                                    }}
                                    className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                                    title={`Copy ${label}`}
                                    disabled={loading || saving}
                                  >
                                    Copy
                                  </button>
                                  <a
                                    href={value}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                                    title={`Open ${label} in new tab`}
                                  >
                                    Open
                                  </a>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* URL Editor Modal */}
      {editingUrlsIndex !== null && editingUrls && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Edit URLs - Image {editingUrlsIndex + 1}
              </h2>
              <button
                onClick={closeUrlEditor}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                disabled={loading || saving}
              >
                <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {[
                { label: 'URL (endPoint)', field: 'endPoint' as keyof typeof editingUrls, description: 'Original/Full size image URL' },
                { label: 'Small URL (surl)', field: 'surl' as keyof typeof editingUrls, description: 'Small transformation (400px width)' },
                { label: 'Medium URL (murl)', field: 'murl' as keyof typeof editingUrls, description: 'Medium transformation (800px width)' },
                { label: 'Large URL (lurl)', field: 'lurl' as keyof typeof editingUrls, description: 'Large transformation (1200px width)' },
              ].map(({ label, field, description }) => (
                <div key={field} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {label}
                    </label>
                    {editingUrls[field] && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(editingUrls[field]);
                            toast.success(`${label} copied to clipboard`);
                          }}
                          className="px-3 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                        >
                          Copy
                        </button>
                        <a
                          href={editingUrls[field]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                        >
                          Open
                        </a>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
                  <textarea
                    value={editingUrls[field]}
                    onChange={(e) => setEditingUrls({ ...editingUrls, [field]: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
                    placeholder={`Enter ${label.toLowerCase()}`}
                    disabled={loading || saving}
                  />
                </div>
              ))}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={closeUrlEditor}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                disabled={loading || saving}
              >
                Cancel
              </button>
              <button
                onClick={saveUrlsFromModal}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading || saving}
              >
                Save URLs
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

